import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ── Etiquetas para cada tipo de notificación ──────────────────────────────────
const NOTIF_LABELS = {
  new_episode:          (p) => `Ep ${p?.episodeNumber} — ${p?.animeTitle || ''}`,
  completed_got_sequel: (p) => `Secuela: ${p?.sequelTitle || p?.animeTitle || ''}`,
  new_anime_in_genre:   ()  => 'Nuevo en tus géneros',
  welcome:              ()  => '¡Bienvenido a Ashveil!',
  team_message:         ()  => 'Del equipo de Ashveil',
};

const NOTIF_ICON = {
  new_episode:          '📺',
  completed_got_sequel: '✨',
  new_anime_in_genre:   '🎌',
  welcome:              '👋',
  team_message:         '💬',
};

const fmtDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
};

// ── Modal de notificaciones ───────────────────────────────────────────────────
function NotifModal({ notifications, onClose }) {
  // Cerrar con Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return createPortal(
    <div className="notif-overlay" onClick={onClose}>
      <div className="notif-modal" onClick={(e) => e.stopPropagation()}>
        {/* Cabecera */}
        <div className="notif-modal__head">
          <h2 className="notif-modal__title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.4rem', verticalAlign: 'middle' }}>
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            Notificaciones
          </h2>
          <button className="notif-modal__close" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>

        {/* Lista */}
        <div className="notif-modal__body">
          {notifications.length === 0 ? (
            <div className="notif-modal__empty">
              <span className="notif-modal__empty-icon">🔔</span>
              <p>No tenés notificaciones todavía</p>
              <span className="notif-modal__empty-sub">Las novedades aparecerán aquí</span>
            </div>
          ) : (
            <ul className="notif-modal__list">
              {notifications.map((n, i) => {
                const p     = n.payload || {};
                const label = NOTIF_LABELS[n.type]?.(p) || n.type;
                const icon  = NOTIF_ICON[n.type]  || '🔔';
                const isMsg = n.type === 'welcome' || n.type === 'team_message';
                const msg   = p.message || '';
                return (
                  <li
                    key={n.id ?? i}
                    className={`notif-modal__item${n.read ? ' notif-modal__item--read' : ''}`}
                  >
                    <span className="notif-modal__icon">{icon}</span>
                    <div className="notif-modal__content">
                      <span className="notif-modal__label">{label}</span>
                      {isMsg && msg && (
                        <p className="notif-modal__msg">{msg}</p>
                      )}
                      {!isMsg && p.animeTitle && (
                        <span className="notif-modal__sub">{p.animeTitle}</span>
                      )}
                      <span className="notif-modal__date">{fmtDate(n.createdAt)}</span>
                    </div>
                    {!n.read && <span className="notif-modal__dot" />}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Campana del navbar ────────────────────────────────────────────────────────
function NavBell() {
  const { notifications, clearNotifications } = useAuth();
  const [open, setOpen] = useState(false);

  const unread = notifications.filter((n) => !n.read).length;

  const handleOpen = () => {
    // Marcar como leídas al abrir (el badge se limpia; las notifs siguen visibles)
    clearNotifications();
    setOpen(true);
  };

  return (
    <>
      <div className="nav__bell">
        <button className="nav__bell-btn" onClick={handleOpen} aria-label="Notificaciones">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          {unread > 0 && (
            <span className="nav__bell-badge">{unread > 9 ? '9+' : unread}</span>
          )}
        </button>
      </div>
      {open && <NotifModal notifications={notifications} onClose={() => setOpen(false)} />}
    </>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const dropRef  = useRef(null);

  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Close dropdown on click outside
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleOutside = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [dropdownOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = inputRef.current?.value.trim();
    if (q) navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const handleLogout = () => {
    setDropdownOpen(false);
    logout();
    navigate('/');
  };

  const navLinkClass = ({ isActive }) =>
    `nav__link${isActive ? ' nav__link--active' : ''}`;

  return (
    <nav className="nav">
      <Link to="/" className="nav__logo">Ash<em>veil</em></Link>

      <div className="nav__links">
        <NavLink to="/directorio" className={navLinkClass}>Directorio</NavLink>
      </div>

      <form className="nav__search" onSubmit={handleSearch}>
        <span className="nav__search-icon">⌕</span>
        <input ref={inputRef} type="text" placeholder="Buscar anime..." />
      </form>

      <div className="nav__actions">
        {user && <NavBell />}
        {user && (
          <div ref={dropRef} className="nav__user-wrap">
            <button
              className={`nav__user-btn${dropdownOpen ? ' nav__user-btn--open' : ''}`}
              title={user.username}
              onClick={() => setDropdownOpen((o) => !o)}
            >
              <div className={`nav__avatar${user.avatar_url ? ' nav__avatar--img' : ''}`}>
                {user.avatar_url
                  ? <img src={user.avatar_url} alt={user.username} referrerPolicy="no-referrer" />
                  : (user.username?.[0]?.toUpperCase() ?? '?')}
              </div>
              <span className="nav__menu-bars" aria-hidden="true">
                <span /><span /><span />
              </span>
            </button>

            {dropdownOpen && (
              <div className="nav__dropdown">
                <div className="nav__dropdown-user">{user.username}</div>
                <div className="nav__dropdown-divider" />
                {user.is_admin && (
                  <>
                    <Link
                      to="/admin"
                      className="nav__dropdown-item"
                      onClick={() => setDropdownOpen(false)}
                    >
                      Panel Admin
                    </Link>
                    <div className="nav__dropdown-divider" />
                  </>
                )}
                <Link
                  to="/mi-lista"
                  className="nav__dropdown-item"
                  onClick={() => setDropdownOpen(false)}
                >
                  Mi lista
                </Link>
                <Link
                  to="/perfil"
                  className="nav__dropdown-item"
                  onClick={() => setDropdownOpen(false)}
                >
                  Perfil
                </Link>
                <div className="nav__dropdown-divider" />
                <button className="nav__dropdown-item nav__dropdown-item--danger" onClick={handleLogout}>
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
